/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  getNonNullResolvedInstructionInput,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const CREATE_POSITION_DISCRIMINATOR = new Uint8Array([
  48, 215, 197, 153, 96, 203, 180, 133,
]);

export function getCreatePositionDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CREATE_POSITION_DISCRIMINATOR,
  );
}

export type CreatePositionInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountPosition extends string | AccountMeta<string> = string,
  TAccountOwner extends string | AccountMeta<string> = string,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? ReadonlyAccount<TAccountPool>
        : TAccountPool,
      TAccountPosition extends string
        ? WritableAccount<TAccountPosition>
        : TAccountPosition,
      TAccountOwner extends string
        ? ReadonlySignerAccount<TAccountOwner> &
            AccountSignerMeta<TAccountOwner>
        : TAccountOwner,
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type CreatePositionInstructionData = {
  discriminator: ReadonlyUint8Array;
  positionId: bigint;
};

export type CreatePositionInstructionDataArgs = { positionId: number | bigint };

export function getCreatePositionInstructionDataEncoder(): FixedSizeEncoder<CreatePositionInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['positionId', getU64Encoder()],
    ]),
    (value) => ({ ...value, discriminator: CREATE_POSITION_DISCRIMINATOR }),
  );
}

export function getCreatePositionInstructionDataDecoder(): FixedSizeDecoder<CreatePositionInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['positionId', getU64Decoder()],
  ]);
}

export function getCreatePositionInstructionDataCodec(): FixedSizeCodec<
  CreatePositionInstructionDataArgs,
  CreatePositionInstructionData
> {
  return combineCodec(
    getCreatePositionInstructionDataEncoder(),
    getCreatePositionInstructionDataDecoder(),
  );
}

export type CreatePositionAsyncInput<
  TAccountPool extends string = string,
  TAccountPosition extends string = string,
  TAccountOwner extends string = string,
  TAccountPayer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  pool: Address<TAccountPool>;
  position?: Address<TAccountPosition>;
  owner: TransactionSigner<TAccountOwner>;
  payer: TransactionSigner<TAccountPayer>;
  systemProgram?: Address<TAccountSystemProgram>;
  positionId: CreatePositionInstructionDataArgs['positionId'];
};

export async function getCreatePositionInstructionAsync<
  TAccountPool extends string,
  TAccountPosition extends string,
  TAccountOwner extends string,
  TAccountPayer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: CreatePositionAsyncInput<
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountPayer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CreatePositionInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountPayer,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: false },
    position: { value: input.position ?? null, isWritable: true },
    owner: { value: input.owner ?? null, isWritable: false },
    payer: { value: input.payer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.position.value) {
    accounts.position.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([112, 111, 115, 105, 116, 105, 111, 110]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount('pool', accounts.pool.value),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'owner',
            accounts.owner.value,
          ),
        ),
        getU64Encoder().encode(
          getNonNullResolvedInstructionInput('positionId', args.positionId),
        ),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('position', accounts.position),
      getAccountMeta('owner', accounts.owner),
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getCreatePositionInstructionDataEncoder().encode(
      args as CreatePositionInstructionDataArgs,
    ),
    programAddress,
  } as CreatePositionInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountPayer,
    TAccountSystemProgram
  >);
}

export type CreatePositionInput<
  TAccountPool extends string = string,
  TAccountPosition extends string = string,
  TAccountOwner extends string = string,
  TAccountPayer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  pool: Address<TAccountPool>;
  position: Address<TAccountPosition>;
  owner: TransactionSigner<TAccountOwner>;
  payer: TransactionSigner<TAccountPayer>;
  systemProgram?: Address<TAccountSystemProgram>;
  positionId: CreatePositionInstructionDataArgs['positionId'];
};

export function getCreatePositionInstruction<
  TAccountPool extends string,
  TAccountPosition extends string,
  TAccountOwner extends string,
  TAccountPayer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: CreatePositionInput<
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountPayer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): CreatePositionInstruction<
  TProgramAddress,
  TAccountPool,
  TAccountPosition,
  TAccountOwner,
  TAccountPayer,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: false },
    position: { value: input.position ?? null, isWritable: true },
    owner: { value: input.owner ?? null, isWritable: false },
    payer: { value: input.payer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('position', accounts.position),
      getAccountMeta('owner', accounts.owner),
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getCreatePositionInstructionDataEncoder().encode(
      args as CreatePositionInstructionDataArgs,
    ),
    programAddress,
  } as CreatePositionInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountPosition,
    TAccountOwner,
    TAccountPayer,
    TAccountSystemProgram
  >);
}

export type ParsedCreatePositionInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    pool: TAccountMetas[0];
    position: TAccountMetas[1];
    owner: TAccountMetas[2];
    payer: TAccountMetas[3];
    systemProgram: TAccountMetas[4];
  };
  data: CreatePositionInstructionData;
};

export function parseCreatePositionInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCreatePositionInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 5,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      pool: getNextAccount(),
      position: getNextAccount(),
      owner: getNextAccount(),
      payer: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getCreatePositionInstructionDataDecoder().decode(instruction.data),
  };
}
